import { useMetrics } from '@cognite/metrics';
import { LOCIZE_NAMESPACES, useTranslation } from '@infield/features/i18n';
import { METRICS_NAMESPACES } from '@infield/features/metrics';
import { getCorrectDateFormat } from '@infield/features/ui/date-range-picker/utils';
import type { Dayjs } from 'dayjs';
import { useContext } from 'react';

import { SchedulesWeekPickerContext } from './schedules-week-picker-context';

// The logic of this component is tested in the schedules-page e2e test,
// as well as unit tests for the helper functions and storybook for the UI component.
interface Props {
  startDate: Dayjs;
  endDate: Dayjs;
  today: Dayjs;
  setDateRange: (dateRange: [Dayjs, Dayjs]) => void;
}

export const SchedulesWeekPicker = ({
  startDate,
  endDate,
  setDateRange,
  today,
}: Props) => {
  const { getPreviousWeekDates, getNextWeekDates, SchedulesWeekPickerUi } =
    useContext(SchedulesWeekPickerContext);

  const { t } = useTranslation(LOCIZE_NAMESPACES.checklist);
  const { i18n } = useTranslation();

  const metrics = useMetrics(METRICS_NAMESPACES.scheduledChecklists);

  const dateFormat = getCorrectDateFormat(i18n.language);

  const isNextWeekDisabled = endDate.isToday();

  const handlePreviousWeekClick = (): void => {
    metrics.track('schedules-week-picker-previous-week-click');
    const [newStartDate, newEndDate] = getPreviousWeekDates(startDate);
    setDateRange([newStartDate, newEndDate]);
  };

  const handleNextWeekClick = (): void => {
    metrics.track('schedules-week-picker-next-week-click');
    const [newStartDate, newEndDate] = getNextWeekDates(startDate, today);
    setDateRange([newStartDate, newEndDate]);
  };

  return (
    <SchedulesWeekPickerUi
      handlePreviousWeekClick={handlePreviousWeekClick}
      handleNextWeekClick={handleNextWeekClick}
      startDate={startDate}
      endDate={endDate}
      isNextWeekDisabled={isNextWeekDisabled}
      translationFunction={t}
      dateFormat={dateFormat}
    />
  );
};
